import {
  AfterContentInit,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SliderModule } from 'primeng/slider';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import * as echarts from 'echarts';
import {
  barChartConfig,
  investmentChartConfig,
} from '@shared/components/investment-calculator/investment-calculator.data';
import {
  InvestmentChartCatergory,
  InvestmentChartConfig,
} from './investment-calculator.model';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';

@Component({
  selector: 'app-investment-calculator',
  imports: [
    CommonModule,
    AngularSvgIconModule,
    SliderModule,
    FormsModule,
    ButtonModule,
    PopoverModule,
    CustomCurrencyPipe,
  ],
  templateUrl: './investment-calculator.component.html',
  styleUrl: './investment-calculator.component.css',
})
export class InvestmentCalculatorComponent
  implements OnChanges, AfterContentInit
{
  @Input() netYieldPercentage: number = 1;
  @Input() currency: string | undefined;

  totalInvestment: number = 0;
  initialInvestment: number = 50000;
  totalRentalIncome: number = 10000;
  valueAppreciation: number = 10000;
  propertyValueGrowth: number = 30;
  expectedAnnualRentalYield: number = 5.5;
  expectedAnnualRentalYieldInitialValue = 5.5;
  readonly propertyValueGrowthInitialValue: number = 30;
  readonly maxInvestment: number = 200000;
  readonly maxPropertyValueGrowth: number = 100;
  readonly maxExpectedAnnualRentalYield: number = 12;
  readonly numberOfYears = 5;
  readonly InvestmentChartCatergory = InvestmentChartCatergory;
  readonly investmentChartConfig: InvestmentChartConfig[] = [
    ...investmentChartConfig,
  ];

  private barChart!: any;
  private barChartConfig!: any;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['netYieldPercentage']) {
      // Set up Investment calculator
      this.expectedAnnualRentalYield = this.netYieldPercentage;
      this.expectedAnnualRentalYieldInitialValue = this.netYieldPercentage;

      const updateChart = !changes['netYieldPercentage'].firstChange;
      this.onInvestmentValueChange(updateChart);
    }
  }

  ngAfterContentInit(): void {
    this.barChartConfig = { ...barChartConfig };
    this.barChartConfig.xAxis.data = this.getChartXAxisData();
    this.barChartConfig.color = this.getChartColors();

    this.updateChart();
  }

  onInvestmentValueChange(updateChart: boolean): void {
    this.onValueGrowthChange(false);
    this.onRentalGrowthChange(updateChart);
  }

  onValueGrowthChange(updateChart: boolean): void {
    this.valueAppreciation =
      (this.initialInvestment * this.propertyValueGrowth) / 100;

    this.totalInvestment =
      this.initialInvestment + this.valueAppreciation + this.totalRentalIncome;

    if (updateChart) {
      this.updateChart();
    }
  }

  onValueGrowthReset(): void {
    this.propertyValueGrowth = this.propertyValueGrowthInitialValue;

    this.updateChart();
  }

  onRentalGrowthChange(updateChart: boolean): void {
    this.totalRentalIncome =
      ((this.initialInvestment * this.expectedAnnualRentalYield) / 100) *
      this.numberOfYears;

    this.totalInvestment =
      this.initialInvestment + this.valueAppreciation + this.totalRentalIncome;

    if (updateChart) {
      this.updateChart();
    }
  }

  onRentalGrowthReset(): void {
    this.expectedAnnualRentalYield = this.expectedAnnualRentalYieldInitialValue;

    this.updateChart();
  }

  private updateChart(): void {
    const dataValues: number[][] = this.getChartRawDataValues();
    const seriesDataValues = (this.barChartConfig.series =
      this.getChartSeries(dataValues));

    this.prepareChart(seriesDataValues);
  }

  private getChartInstance() {
    if (this.barChart) {
      return this.barChart;
    }

    return echarts.init(document.getElementById('chart-area'));
  }

  private getChartXAxisData(): string[] {
    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: this.numberOfYears },
      (_, i) => `${currentYear + i + 1}`
    );
  }

  private getChartColors(): string[] {
    return this.investmentChartConfig.map(
      (incomeCategory) => incomeCategory.color
    );
  }

  private getChartRawDataValues(): number[][] {
    // Set Investment raw data
    const rawDataInvestment = Array.from(
      { length: this.numberOfYears },
      () => this.initialInvestment
    );

    // Set Value appreciation
    const propertyValueGrowthPerYear =
      this.propertyValueGrowth / this.numberOfYears;

    const rawDataAppreciation = Array.from(
      { length: this.numberOfYears },
      (_, i) =>
        ((this.initialInvestment * propertyValueGrowthPerYear) / 100) * (i + 1)
    );

    // Set Total rental income raw data
    const rawDataRental = Array.from(
      { length: this.numberOfYears },
      (_, i) =>
        ((this.initialInvestment * this.expectedAnnualRentalYield) / 100) *
        (i + 1)
    );

    return [rawDataInvestment, rawDataAppreciation, rawDataRental];
  }

  private getChartSeries(rawData: number[][]): any[] {
    return this.investmentChartConfig.map((incomeCategory) => ({
      name: incomeCategory.label,
      type: 'bar',
      stack: 'total',
      barWidth: '60%',
      itemStyle: {
        borderRadius: incomeCategory.borderRadius,
      },
      label: {
        show: false,
      },
      data: rawData[incomeCategory.category],
    }));
  }

  private prepareChart(seriesDataValues: number[][]) {
    const chart = this.getChartInstance();
    const configOptions = { ...this.barChartConfig };
    (configOptions as any).series = seriesDataValues;
    // this.chartDataValues = dataValues;

    chart.setOption(configOptions);
    // chart.on('mouseover', this.updateChartMouseOver);
    // chart.on('mouseout', this.updateChartMouseOut);
    this.barChart = chart;

    // Resize chart
    const container = document.getElementById('chart-area-container');
    if (container) {
      new ResizeObserver(() => chart.resize()).observe(container);
    }
  }
}
